import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import LoginForm from './components/LoginForm';
import SocialLogin from './components/SocialLogin';
import SecurityIndicators from './components/SecurityIndicators';
import LoginHeader from './components/LoginHeader';
import CreateAccountPrompt from './components/CreateAccountPrompt';
import Logo from '../../components/Logo';
import { LoginFormData, LoginErrors } from './types';
import MobileAppDownloads from '../../components/MobileAppDownloads';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<LoginErrors>({});
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  // Handle password reset callback from email link
  useEffect(() => {
    const handlePasswordReset = async () => {
      // If we're on the wrong host/port (e.g., localhost:3000 when we should be on 172.16.20.104:4028),
      // redirect to the correct origin. Check if we're on localhost:3000 or if the origin doesn't match expected
      const currentOrigin = window.location.origin;
      const isWrongPort = (window.location.hostname === 'localhost' && window.location.port === '3000') ||
        (window.location.hostname === 'localhost' && !window.location.port);

      // If we're on the wrong origin, try to redirect to the correct one
      // For development, check if we should be on a different IP/port
      if (isWrongPort) {
        // Try to get the correct origin from sessionStorage or use a fallback
        const savedOrigin = sessionStorage.getItem('app_origin');
        const correctOrigin = savedOrigin || 'http://172.16.20.104:4028';
        const newUrl = `${correctOrigin}${window.location.pathname}${window.location.search}${window.location.hash}`;
        console.log('Redirecting from wrong origin to:', newUrl);
        window.location.href = newUrl;
        return;
      }

      // Save the current origin for future redirects
      if (currentOrigin && !currentOrigin.includes('localhost:3000')) {
        sessionStorage.setItem('app_origin', currentOrigin);
      }

      // Check for error in URL hash (e.g., expired token)
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const error = hashParams.get('error');
      const errorDescription = hashParams.get('error_description');

      if (error) {
        console.error('Password reset error:', error, errorDescription);
        if (error === 'otp_expired' || error === 'access_denied') {
          setResetError('The password reset link has expired. Please request a new one from the Forgot Password page.');
          setIsResettingPassword(true); // Show the form so they can see the error and request a new link
        } else {
          setResetError(errorDescription || 'Invalid reset link. Please request a new one.');
          setIsResettingPassword(true);
        }
        // Clear the URL hash but keep the error info
        window.history.replaceState({}, document.title, '/login?reset=true&error=' + error);
        return;
      }

      // Check if we have a reset token in the URL hash
      const accessToken = hashParams.get('access_token');
      const type = hashParams.get('type');
      const refreshToken = hashParams.get('refresh_token');

      // Also check query params for reset flag
      const resetParam = searchParams.get('reset');

      if ((type === 'recovery' && accessToken) || resetParam === 'true') {
        // If we have a token, we need to exchange it for a session
        if (accessToken && type === 'recovery' && refreshToken) {
          try {
            // Set the session from the token
            const { data, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken,
            });

            if (sessionError) {
              console.error('Session error:', sessionError);
              setResetError('Invalid or expired reset link. Please request a new one.');
              setIsResettingPassword(true);
              window.history.replaceState({}, document.title, '/login?reset=true');
              return;
            }
          } catch (error: any) {
            console.error('Error setting session:', error);
            setResetError('Failed to process reset link. Please try again.');
            setIsResettingPassword(true);
            window.history.replaceState({}, document.title, '/login?reset=true');
            return;
          }
        }

        setIsResettingPassword(true);
        // Clear the URL hash and params
        window.history.replaceState({}, document.title, '/login?reset=true');
      }
    };

    handlePasswordReset();
  }, [searchParams]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    setResetSuccess(false);

    if (!newPassword || newPassword.length < 8) {
      setResetError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match');
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) throw error;

      setResetSuccess(true);
      setNewPassword('');
      setConfirmPassword('');

      // Redirect to login after 2 seconds
      setTimeout(() => {
        setIsResettingPassword(false);
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      console.error('Password reset error:', error);
      setResetError(error.message || 'Failed to reset password. The link may have expired.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    setErrors({});

    try {
      // Always trim: trailing/leading whitespace can cause email detection to fail
      const input = (data.emailOrPhone || '').trim();

      const isEmailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
      const digitsOnly = input.replace(/[^\d]/g, '');
      const cleanedAlnumLower = input.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      const isAllDigits = digitsOnly.length > 0 && /^[\d+]+$/.test(input.replace(/\s/g, ''));

      const signInWithCandidates = async (emails: string[]) => {
        let lastError: any = null;
        for (const email of emails) {
          const { data: authData, error: signInError } = await supabase.auth.signInWithPassword({
            email,
            password: data.password,
          });
          if (!signInError && authData?.user) return authData;

          lastError = signInError;
          const msg = (signInError?.message || '').toLowerCase();
          const isInvalidCreds =
            msg.includes('invalid login credentials') || msg.includes('invalid credentials');

          // Only try the next candidate for invalid-credential style errors.
          if (!isInvalidCreds) throw signInError;
        }
        throw lastError || new Error('Login failed');
      };

      const lookupPhoneByNationalId = async (nationalId: string) => {
        // Best-effort lookup to support legacy/member self-registration where auth email is phone_...@phone.local
        // Only called after nid_ login fails with invalid creds.
        try {
          const cleaned = (nationalId || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
          if (!cleaned) return null;

          const { data: profile, error } = await supabase
            .from('user_profiles')
            .select('phone')
            .eq('national_id', cleaned)
            .maybeSingle();

          if (error) return null;
          return profile?.phone || null;
        } catch {
          return null;
        }
      };

      // Build candidates to match mobile behavior:
      // - group creators (group_admin) use real email auth (email input)
      // - members (and members assigned admin/clerk in a group) can use phone or national ID
      let authData: any;
      if (isEmailFormat) {
        authData = await signInWithCandidates([input]);
      } else {
        const isLikelyLocalPhone = isAllDigits && digitsOnly.length === 7;
        const isLikelyCountryPhone = digitsOnly.startsWith('678') && digitsOnly.length >= 10;
        const isPhoneInput =
          isLikelyLocalPhone || isLikelyCountryPhone || (input.startsWith('+') && digitsOnly.length >= 7 && digitsOnly.length <= 15);
        const isNationalIdInput =
          !isPhoneInput && isAllDigits && digitsOnly.length >= 5 && digitsOnly.length <= 8;

        // Phone auth email candidates (try +678 first like mobile, then local fallback)
        const phoneCandidates: string[] = [];
        if (digitsOnly.length) {
          if (digitsOnly.length === 7) {
            phoneCandidates.push(`phone_678${digitsOnly}@phone.local`);
            phoneCandidates.push(`phone_${digitsOnly}@phone.local`);
          } else if (digitsOnly.startsWith('678')) {
            phoneCandidates.push(`phone_${digitsOnly}@phone.local`);
            if (digitsOnly.length > 3) {
              phoneCandidates.push(`phone_${digitsOnly.substring(3)}@phone.local`);
            }
          } else {
            // Default: use digits as-is, then try with +678 prefix
            phoneCandidates.push(`phone_${digitsOnly}@phone.local`);
            phoneCandidates.push(`phone_678${digitsOnly}@phone.local`);
          }
        }

        // National ID auth email candidates
        // - Prefer digit-only mapping (common for 5-8 digit NIDs)
        // - Also try alphanumeric mapping (some deployments use letters/hyphens)
        const nationalIdCandidate: string[] = [];
        if (digitsOnly) nationalIdCandidate.push(`nid_${digitsOnly}@nid.local`);
        if (cleanedAlnumLower && cleanedAlnumLower !== digitsOnly) {
          nationalIdCandidate.push(`nid_${cleanedAlnumLower}@nid.local`);
        }

        if (isNationalIdInput) {
          try {
            authData = await signInWithCandidates(nationalIdCandidate);
          } catch (e: any) {
            // If nid_ mapping fails, try resolving the NID to a phone-based auth email (member self-registration path)
            const phone = await lookupPhoneByNationalId(input);
            const phoneDigits = (phone || '').replace(/[^\d]/g, '');
            if (phoneDigits) {
              const mappedPhoneCandidates: string[] = [];
              if (phoneDigits.length === 7) {
                mappedPhoneCandidates.push(`phone_678${phoneDigits}@phone.local`);
                mappedPhoneCandidates.push(`phone_${phoneDigits}@phone.local`);
              } else if (phoneDigits.startsWith('678')) {
                mappedPhoneCandidates.push(`phone_${phoneDigits}@phone.local`);
                if (phoneDigits.length > 3) mappedPhoneCandidates.push(`phone_${phoneDigits.substring(3)}@phone.local`);
              } else {
                mappedPhoneCandidates.push(`phone_${phoneDigits}@phone.local`);
                mappedPhoneCandidates.push(`phone_678${phoneDigits}@phone.local`);
              }

              authData = await signInWithCandidates(mappedPhoneCandidates);
            } else {
              throw e;
            }
          }
        } else if (isPhoneInput) {
          try {
            authData = await signInWithCandidates(phoneCandidates);
          } catch (e: any) {
            // Ambiguous 5-8 digit numeric input could be a National ID.
            // If phone auth fails, try National ID like mobile.
            const ambiguousNumeric = isAllDigits && digitsOnly.length >= 5 && digitsOnly.length <= 8;
            if (ambiguousNumeric) {
              try {
                authData = await signInWithCandidates(nationalIdCandidate);
              } catch (e2: any) {
                // Same fallback: if it's a National ID for a phone-registered member
                const phone = await lookupPhoneByNationalId(input);
                const phoneDigits = (phone || '').replace(/[^\d]/g, '');
                if (phoneDigits) {
                  const mappedPhoneCandidates: string[] = [];
                  if (phoneDigits.length === 7) {
                    mappedPhoneCandidates.push(`phone_678${phoneDigits}@phone.local`);
                    mappedPhoneCandidates.push(`phone_${phoneDigits}@phone.local`);
                  } else if (phoneDigits.startsWith('678')) {
                    mappedPhoneCandidates.push(`phone_${phoneDigits}@phone.local`);
                    if (phoneDigits.length > 3) mappedPhoneCandidates.push(`phone_${phoneDigits.substring(3)}@phone.local`);
                  } else {
                    mappedPhoneCandidates.push(`phone_${phoneDigits}@phone.local`);
                    mappedPhoneCandidates.push(`phone_678${phoneDigits}@phone.local`);
                  }

                  authData = await signInWithCandidates(mappedPhoneCandidates);
                } else {
                  throw e2;
                }
              }
            } else {
              throw e;
            }
          }
        } else {
          // If it's neither email nor a valid numeric credential, fail fast
          throw new Error('Please enter a valid email, phone number, or national ID.');
        }
      }

      if (!authData?.user) {
        throw new Error('Login successful but no user data returned');
      }

      // Fetch user profile to determine role
      const { data: profile, error: profileError } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', authData.user.id)
        .single();

      if (profileError) {
        console.error('Error fetching user profile:', profileError);
        // Fallback navigation when profile read fails (RLS/network/etc):
        // If the user is an admin/clerk in any group OR is a group creator, send to admin dashboard.
        try {
          const [{ data: gm }, { data: createdGroups }] = await Promise.all([
            supabase
              .from('group_members')
              .select('group_id')
              .eq('user_id', authData.user.id)
              .in('role', ['admin', 'clerk'])
              .limit(1)
              .maybeSingle(),
            supabase
              .from('groups')
              .select('id')
              .eq('created_by', authData.user.id)
              .limit(1),
          ]);

          if (gm?.group_id || (createdGroups && createdGroups.length > 0)) {
            navigate('/admin-dashboard');
          } else {
            navigate('/member-dashboard');
          }
        } catch {
          navigate('/member-dashboard');
        }
        return;
      }

      // Enforce: admins must login with EMAIL only (members can login with phone/national ID)
      if (!isEmailFormat && (profile?.role === 'group_admin' || profile?.role === 'super_admin')) {
        await supabase.auth.signOut();
        setErrors({
          general: 'Admins must sign in with email. Please use your email address to log in.',
        });
        return;
      }

      // Navigate based on role
      if (profile?.role === 'super_admin') {
        navigate('/admin-dashboard'); // Super admin dashboard with platform revenue
      } else if (profile?.role === 'group_admin') {
        navigate('/group-management'); // Group admin manages groups
      } else if (profile?.role === 'group_creator') {
        navigate('/admin-dashboard'); // Group creator uses admin dashboard
      } else {
        // Member: if they are an admin/clerk in any group (group_members.role), treat as admin UI
        try {
          const { data: gm } = await supabase
            .from('group_members')
            .select('group_id')
            .eq('user_id', authData.user.id)
            .in('role', ['admin', 'clerk'])
            .limit(1)
            .maybeSingle();

          if (gm?.group_id) {
            navigate('/admin-dashboard');
          } else {
            navigate('/member-dashboard'); // Members see their dashboard
          }
        } catch {
          navigate('/member-dashboard');
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      const msg = (error?.message || '').toLowerCase();
      if (msg.includes('email not confirmed')) {
        setErrors({
          general:
            'Your account is not activated yet (email not confirmed). Please contact your group admin or support to activate your account.',
        });
        return;
      }

      // Provide a clearer message for National ID logins: many members are created with a phone-based auth email,
      // so "invalid credentials" often means the password was never set/reset for the member.
      try {
        const isInvalidCreds = msg.includes('invalid login credentials') || msg.includes('invalid credentials');
        const input = (data.emailOrPhone || '').trim();
        const digitsOnly = input.replace(/[^\d]/g, '');
        const looksLikeNationalId = digitsOnly.length >= 5 && digitsOnly.length <= 8 && /^[\d+]+$/.test(input.replace(/\s/g, ''));
        if (isInvalidCreds && looksLikeNationalId) {
          const cleaned = digitsOnly.replace(/[^\d]/g, '');
          if (cleaned) {
            const { data: profile } = await supabase
              .from('user_profiles')
              .select('id')
              .eq('national_id', cleaned)
              .maybeSingle();

            if (profile?.id) {
              setErrors({
                general:
                  'Account found for this National ID, but the password is incorrect (or not set yet). ' +
                  'Please ask your super admin to reset your password from Settings → Users → Set Password.',
              });
              return;
            }
          }
        }
      } catch {
        // ignore lookup failures and fall back to generic error
      }

      setErrors({
        general: error.message || 'Invalid email / national ID / phone or password. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = async (provider: string) => {
    setIsLoading(true);
    setErrors({});

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: provider as any,
        options: {
          redirectTo: `${window.location.origin}/member-dashboard`
        }
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Social login error:', error);
      setErrors({
        general: `Failed to sign in with ${provider}. Please try again.`
      });
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Login - communitySaving</title>
        <meta name="description" content="Sign in to your communitySaving account to manage community contributions and track financial activities." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-teal-600 via-teal-500 to-emerald-500 flex flex-col">
        {/* Animated Particles Background with Vibrant Colors */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
          <div className="absolute w-3 h-3 bg-yellow-400/40 rounded-full animate-float" style={{ top: '10%', left: '5%', animationDelay: '0s', animationDuration: '6s' }}></div>
          <div className="absolute w-4 h-4 bg-orange-400/35 rounded-full animate-float" style={{ top: '20%', left: '15%', animationDelay: '1s', animationDuration: '8s' }}></div>
          <div className="absolute w-2 h-2 bg-purple-400/30 rounded-full animate-float" style={{ top: '40%', left: '10%', animationDelay: '2s', animationDuration: '7s' }}></div>
          <div className="absolute w-5 h-5 bg-teal-400/30 rounded-full animate-float" style={{ top: '60%', left: '8%', animationDelay: '3s', animationDuration: '9s' }}></div>
          <div className="absolute w-3 h-3 bg-pink-400/25 rounded-full animate-float" style={{ top: '80%', left: '12%', animationDelay: '4s', animationDuration: '6s' }}></div>
          <div className="absolute w-4 h-4 bg-yellow-400/35 rounded-full animate-float" style={{ top: '15%', right: '8%', animationDelay: '1.5s', animationDuration: '7s' }}></div>
          <div className="absolute w-3 h-3 bg-emerald-400/30 rounded-full animate-float" style={{ top: '35%', right: '15%', animationDelay: '2.5s', animationDuration: '8s' }}></div>
          <div className="absolute w-5 h-5 bg-blue-400/25 rounded-full animate-float" style={{ top: '55%', right: '10%', animationDelay: '3.5s', animationDuration: '6s' }}></div>
          <div className="absolute w-3 h-3 bg-orange-400/30 rounded-full animate-float" style={{ top: '75%', right: '12%', animationDelay: '4.5s', animationDuration: '9s' }}></div>
          {/* Additional colorful accent shapes */}
          <div className="absolute w-16 h-16 bg-gradient-to-br from-yellow-400/20 to-orange-400/20 rounded-full blur-xl" style={{ top: '30%', left: '20%', animationDelay: '2s' }}></div>
          <div className="absolute w-20 h-20 bg-gradient-to-br from-teal-400/15 to-emerald-400/15 rounded-full blur-xl" style={{ top: '70%', right: '25%', animationDelay: '3s' }}></div>
        </div>

        {/* Decorative Navigation Strip - Melanesian/Polynesian Tapa Pattern */}
        <div className="w-full h-3 bg-gradient-to-r from-yellow-400 via-orange-400 to-teal-500 relative overflow-hidden shadow-lg">
          <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
            <defs>
              {/* Traditional Pacific Island Tapa Cloth Pattern */}
              <pattern id="tapaPattern" x="0" y="0" width="60" height="8" patternUnits="userSpaceOnUse">
                {/* Zigzag patterns common in Polynesian tapa */}
                <polyline points="0,2 3,6 6,2 9,6 12,2" stroke="white" strokeWidth="0.6" fill="none" opacity="0.5" />
                {/* Triangular motifs */}
                <polygon points="15,2 17,6 19,2" fill="white" opacity="0.4" />
                <polygon points="21,2 23,6 25,2" fill="white" opacity="0.3" />
                {/* Diagonal lines - traditional weave pattern */}
                <line x1="28" y1="1" x2="33" y2="7" stroke="white" strokeWidth="0.5" opacity="0.4" />
                <line x1="30" y1="1" x2="35" y2="7" stroke="white" strokeWidth="0.5" opacity="0.3" />
                {/* Diamond shapes */}
                <polygon points="40,4 42,2 44,4 42,6" fill="white" opacity="0.4" />
                {/* Dots - representing stars/navigation */}
                <circle cx="48" cy="2" r="0.8" fill="white" opacity="0.5" />
                <circle cx="48" cy="6" r="0.8" fill="white" opacity="0.5" />
                <circle cx="52" cy="4" r="1" fill="white" opacity="0.4" />
                {/* Wave motif */}
                <path d="M55,4 Q56,2 57,4 T59,4" stroke="white" strokeWidth="0.6" fill="none" opacity="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#tapaPattern)" />
          </svg>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex">
          {/* Left Side - Branding Section */}
          <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-teal-600 via-emerald-500 to-teal-700">
            {/* Colorful Decorative Shapes */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-10 -left-20 w-64 h-64 bg-yellow-400/20 rounded-full blur-3xl"></div>
              <div className="absolute bottom-20 -right-20 w-80 h-80 bg-orange-400/15 rounded-full blur-3xl"></div>
              <div className="absolute top-1/2 left-1/3 w-48 h-48 bg-purple-400/10 rounded-full blur-2xl"></div>
            </div>
            {/* Melanesian & Polynesian Decorative Pattern Overlay */}
            <div className="absolute inset-0 opacity-10">
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  {/* Traditional Pacific Island Geometric Pattern */}
                  <pattern id="melanesianPattern" width="80" height="80" patternUnits="userSpaceOnUse">
                    {/* Central star motif - navigation/wayfinding */}
                    <circle cx="40" cy="40" r="3" fill="white" opacity="0.7" />
                    <circle cx="20" cy="20" r="2" fill="yellow" opacity="0.5" />
                    <circle cx="60" cy="20" r="2" fill="orange" opacity="0.5" />
                    <circle cx="20" cy="60" r="2" fill="teal" opacity="0.5" />
                    <circle cx="60" cy="60" r="2" fill="white" opacity="0.5" />

                    {/* Triangular patterns - traditional tattoo/tapa motifs */}
                    <polygon points="40,25 45,35 35,35" fill="white" opacity="0.4" />
                    <polygon points="40,55 35,45 45,45" fill="yellow" opacity="0.3" />
                    <polygon points="25,40 35,35 35,45" fill="orange" opacity="0.3" />
                    <polygon points="55,40 45,35 45,45" fill="teal" opacity="0.3" />

                    {/* Zigzag weave pattern */}
                    <polyline points="0,10 5,5 10,10 15,5 20,10" stroke="white" strokeWidth="1" fill="none" opacity="0.4" />
                    <polyline points="60,70 65,65 70,70 75,65 80,70" stroke="white" strokeWidth="1" fill="none" opacity="0.4" />

                    {/* Diamond/rhombus shapes - common in Pacific art */}
                    <polygon points="10,40 15,45 10,50 5,45" stroke="white" strokeWidth="0.5" fill="none" opacity="0.5" />
                    <polygon points="70,30 75,35 70,40 65,35" stroke="orange" strokeWidth="0.5" fill="none" opacity="0.4" />
                  </pattern>

                  {/* Ocean wave pattern - representing the Pacific */}
                  <pattern id="wavePattern" width="100" height="30" patternUnits="userSpaceOnUse">
                    <path d="M0,15 Q12.5,5 25,15 T50,15 T75,15 T100,15" stroke="white" strokeWidth="1.5" fill="none" opacity="0.3" />
                    <path d="M0,20 Q12.5,12 25,20 T50,20 T75,20 T100,20" stroke="teal" strokeWidth="1" fill="none" opacity="0.2" />
                  </pattern>
                </defs>
                <rect width="100%" height="50%" fill="url(#melanesianPattern)" />
                <rect y="50%" width="100%" height="50%" fill="url(#wavePattern)" />
              </svg>
            </div>

            {/* Branding Content */}
            <div className="relative z-10 p-12 flex flex-col justify-between w-full">
              <div className="space-y-3">
                <Logo size={56} color="white" variant="full" />
                <p className="text-white/80 text-sm font-medium">Community Savings Platform</p>
              </div>

              <div className="text-white space-y-6 max-w-lg">
                <h1 className="text-5xl font-bold leading-tight drop-shadow-lg">
                  Empowering Communities Through Shared Savings
                </h1>
                <p className="text-xl text-white/95 leading-relaxed">
                  Secure, transparent, and easy-to-use platform for managing group contributions and building financial futures together.
                </p>
                <div className="grid grid-cols-1 gap-3 pt-6">
                  {/* Security Card with Pacific Pattern Border */}
                  <div className="relative flex items-center gap-3 text-white bg-gradient-to-r from-yellow-400/25 to-orange-400/20 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105 hover:shadow-lg overflow-hidden">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Icon name="Shield" size={22} color="white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-base font-bold">Bank-level Security</div>
                      <div className="text-xs text-white/90">Your data is always protected</div>
                    </div>
                    {/* Traditional triangular pattern border */}
                    <svg className="absolute bottom-0 left-0 w-full h-2" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                      <pattern id="triPattern1" x="0" y="0" width="20" height="8" patternUnits="userSpaceOnUse">
                        <polygon points="0,8 5,0 10,8" fill="white" opacity="0.2" />
                        <polygon points="10,8 15,0 20,8" fill="white" opacity="0.15" />
                      </pattern>
                      <rect width="100%" height="100%" fill="url(#triPattern1)" />
                    </svg>
                  </div>

                  {/* Community Card with Wave Pattern Border */}
                  <div className="relative flex items-center gap-3 text-white bg-gradient-to-r from-teal-400/25 to-emerald-400/20 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105 hover:shadow-lg overflow-hidden">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-400 to-emerald-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Icon name="Users" size={22} color="white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-base font-bold">Community First</div>
                      <div className="text-xs text-white/90">Built for groups, by the community</div>
                    </div>
                    {/* Ocean wave pattern border */}
                    <svg className="absolute bottom-0 left-0 w-full h-2" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                      <pattern id="wavePattern2" x="0" y="0" width="40" height="8" patternUnits="userSpaceOnUse">
                        <path d="M0,4 Q5,0 10,4 T20,4 T30,4 T40,4" stroke="white" strokeWidth="1.5" fill="none" opacity="0.25" />
                      </pattern>
                      <rect width="100%" height="100%" fill="url(#wavePattern2)" />
                    </svg>
                  </div>

                  {/* Growth Card with Diamond Pattern Border */}
                  <div className="relative flex items-center gap-3 text-white bg-gradient-to-r from-purple-400/25 to-pink-400/20 backdrop-blur-sm rounded-xl p-4 border border-white/20 hover:border-white/40 transition-all duration-300 hover:scale-105 hover:shadow-lg overflow-hidden">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Icon name="TrendingUp" size={22} color="white" />
                    </div>
                    <div className="flex-1">
                      <div className="text-base font-bold">Financial Growth</div>
                      <div className="text-xs text-white/90">Track and grow your savings</div>
                    </div>
                    {/* Diamond/zigzag pattern border */}
                    <svg className="absolute bottom-0 left-0 w-full h-2" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                      <pattern id="diamondPattern3" x="0" y="0" width="16" height="8" patternUnits="userSpaceOnUse">
                        <polyline points="0,4 4,0 8,4 12,0 16,4" stroke="white" strokeWidth="1.2" fill="none" opacity="0.2" />
                      </pattern>
                      <rect width="100%" height="100%" fill="url(#diamondPattern3)" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Login Form */}
          <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12 bg-gradient-to-br from-gray-50 via-teal-50/30 to-emerald-50/30">
            <div className="w-full max-w-md">
              {/* Mobile Logo & Branding */}
              <div className="lg:hidden mb-10 text-center">
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    <div className="absolute -inset-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full blur-lg opacity-30"></div>
                    <Logo size={48} color="default" variant="full" />
                  </div>
                </div>
                <h2 className="text-xl font-bold bg-gradient-to-r from-teal-600 to-emerald-600 bg-clip-text text-transparent mb-2">Community Savings Platform</h2>
                <p className="text-sm text-gray-600">Empowering Communities Together</p>
              </div>

              <div className="bg-white rounded-2xl shadow-2xl p-8 border-2 border-teal-100 hover:border-teal-200 transition-all duration-300">
                {isResettingPassword ? (
                  <div className="space-y-6">
                    <div className="text-center">
                      <h1 className="text-2xl font-bold text-foreground mb-2">Reset Your Password</h1>
                      <p className="text-muted-foreground text-sm">
                        Enter your new password below
                      </p>
                    </div>

                    {resetError && (
                      <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
                        <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-destructive font-medium">{resetError}</p>
                        </div>
                      </div>
                    )}

                    {resetSuccess && (
                      <div className="p-4 bg-green-50 border border-green-200 rounded-md flex items-start gap-3">
                        <Icon name="CheckCircle" size={20} color="var(--color-green-600)" className="flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-green-800 font-medium">
                            Password reset successfully! Redirecting to login...
                          </p>
                        </div>
                      </div>
                    )}

                    <form onSubmit={handlePasswordReset} className="space-y-4">
                      <Input
                        type="password"
                        label="New Password"
                        placeholder="Enter your new password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        disabled={isLoading || resetSuccess}
                        className="w-full"
                        autoFocus
                      />

                      <Input
                        type="password"
                        label="Confirm Password"
                        placeholder="Confirm your new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        disabled={isLoading || resetSuccess}
                        className="w-full"
                      />

                      <div className="space-y-2">
                        <Button
                          type="submit"
                          variant="default"
                          size="lg"
                          fullWidth
                          loading={isLoading}
                          disabled={resetSuccess}
                          iconName="Key"
                          iconPosition="right"
                        >
                          Reset Password
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          fullWidth
                          onClick={() => {
                            setIsResettingPassword(false);
                            setNewPassword('');
                            setConfirmPassword('');
                            setResetError('');
                            window.history.replaceState({}, document.title, '/login');
                          }}
                          iconName="ArrowLeft"
                        >
                          Back to Login
                        </Button>
                      </div>
                    </form>
                  </div>
                ) : (
                  <>
                    <LoginHeader />

                    <LoginForm
                      onSubmit={handleLogin}
                      isLoading={isLoading}
                      errors={errors}
                    />

                    <MobileAppDownloads className="mt-6" />
                  </>
                )}

                {/* Divider before Social Login */}
                <div className="my-6">
                  <svg className="w-full h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 20">
                    {/* Soft curved transition */}
                    <path d="M 0,10 Q 100,5 200,10 Q 300,15 400,10"
                      stroke="currentColor" strokeWidth="1" fill="none" opacity="0.15" strokeLinecap="round" />

                    {/* Zig-zag accent */}
                    <path d="M 150,10 L 160,6 L 170,10 L 180,6 L 190,10 L 200,6 L 210,10 L 220,6 L 230,10 L 240,6 L 250,10"
                      stroke="currentColor" strokeWidth="0.8" fill="none" opacity="0.1" />

                    {/* Dotted sand texture */}
                    <g fill="currentColor" opacity="0.1">
                      <circle cx="80" cy="10" r="1" />
                      <circle cx="320" cy="10" r="1" />
                      <circle cx="120" cy="8" r="0.6" />
                      <circle cx="280" cy="12" r="0.6" />
                    </g>
                  </svg>
                </div>

                <SocialLogin
                  onSocialLogin={handleSocialLogin}
                  isLoading={isLoading}
                />

                {/* Divider before Account Prompt */}
                <div className="my-6">
                  <svg className="w-full h-6" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 20">
                    <path d="M 0,10 Q 100,15 200,10 Q 300,5 400,10"
                      stroke="currentColor" strokeWidth="1" fill="none" opacity="0.15" strokeLinecap="round" />
                    <g fill="currentColor" opacity="0.1">
                      <circle cx="100" cy="10" r="1" />
                      <circle cx="200" cy="10" r="1" />
                      <circle cx="300" cy="10" r="1" />
                    </g>
                  </svg>
                </div>

                <CreateAccountPrompt />

                <SecurityIndicators />
              </div>

              <div className="mt-8 text-center space-y-3">
                <div className="flex items-center justify-center gap-6 text-xs text-muted-foreground">
                  <a href="#" className="hover:text-primary transition-colors">Terms of Service</a>
                  <span className="text-border">•</span>
                  <a href="#" className="hover:text-primary transition-colors">Privacy Policy</a>
                  <span className="text-border">•</span>
                  <a href="#" className="hover:text-primary transition-colors">Support</a>
                </div>
                <p className="text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <span>Powered by</span>
                  <a href='https://innovatelhubltd.com/' target='_blank' rel='noopener noreferrer' className="font-semibold text-primary hover:underline inline-flex items-center gap-1">
                    Innovation Hub Solutions
                    <Icon name="ExternalLink" size={12} />
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
